import { useQuery } from '@tanstack/react-query';
import * as Sentry from '@sentry/browser';

import { ocppService } from '../services/ocpp/OcppService';
import { SessionLocation } from '../utils/activeSessions';

/** The idle window outlives the charge, so it is polled at the same cadence. */
export const IDLE_SESSION_POLL_MS = 10000;

export const IDLE_SESSION_QUERY_KEY = ['sessions', 'idle-location'];

/**
 * Where the car is still plugged in after its charge finished, if it is. The
 * driver is billed for that window, so the screen showing it has to stay
 * reachable — `enabled` is false while a charge is running, which is a live
 * session's own business.
 */
export const useIdleSessionLocation = (enabled: boolean): SessionLocation | null => {
  const { data } = useQuery({
    queryKey: IDLE_SESSION_QUERY_KEY,
    queryFn: async (): Promise<SessionLocation | null> => {
      try {
        const last = await ocppService.getLastUserSession();
        if (!last?.sessionId || !last?.stationId || !last?.connectorId) {
          return null;
        }

        const idle = await ocppService.getIdlePreview(last.sessionId);
        if (!idle?.isIdle) {
          return null;
        }

        return {
          stationId: String(last.stationId),
          connectorId: String(last.connectorId),
        };
      } catch (error) {
        // A failed poll hides the charging tab, so it should not be silent.
        Sentry.captureException({
          message: '[ocppService][idleSessionLocation]',
          error,
        });
        return null;
      }
    },
    enabled,
    refetchInterval: IDLE_SESSION_POLL_MS,
    refetchIntervalInBackground: false,
    staleTime: 0,
  });

  // A charge started since the last poll: that answer is stale by definition.
  return enabled ? (data ?? null) : null;
};
